import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'

import NavBar from '../components/layout/NavBar'
import DiscoverBookingModal from '../components/booking/DiscoverBookingModal'
import { discoverPros, listProsCategories } from '../api/prosReader'
import ProPublicCard from '../pros/components/ProPublicCard'
import { CategoryIcon } from './pros/categoryIcons'
import type { ProCardViewModel } from '../pros/components/types'
import type { ProCategory, ProDiscoverItem } from '../types/pros'

type SortKey = 'newest' | 'reputation' | 'alphabetical' | 'soonest' | 'distance'

function parseBool(value: string | null): boolean {
  return value === '1' || String(value || '').toLowerCase() === 'true'
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US').format(value || 0)
}

export default function Pros() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [categories, setCategories] = useState<ProCategory[]>([])
  const [categoriesLoading, setCategoriesLoading] = useState(false)
  const [expandedCategorySlugs, setExpandedCategorySlugs] = useState<Set<string>>(new Set())
  const [items, setItems] = useState<ProDiscoverItem[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [hasNext, setHasNext] = useState(false)
  const [discoverBookingOpen, setDiscoverBookingOpen] = useState(false)
  const [discoverBookingTarget, setDiscoverBookingTarget] = useState<{ hostRef: string; displayName: string } | null>(null)

  const q = searchParams.get('q') || ''
  const category = searchParams.get('category') || ''
  const subcategory = searchParams.get('subcategory') || ''
  const sort = (searchParams.get('sort') || 'newest') as SortKey
  const openNow = parseBool(searchParams.get('open_now'))
  const bookableToday = parseBool(searchParams.get('bookable_today'))
  const hasBooking = parseBool(searchParams.get('has_booking'))

  useEffect(() => {
    let ignore = false
    const loadCategories = async () => {
      setCategoriesLoading(true)
      try {
        const rows = await listProsCategories()
        if (!ignore) {
          setCategories(rows)
          setExpandedCategorySlugs((prev) => {
            if (category && rows.some((item) => item.slug === category)) {
              const next = new Set(prev)
              next.add(category)
              return next
            }
            return prev
          })
        }
      } catch {
        if (!ignore) setCategories([])
      } finally {
        if (!ignore) setCategoriesLoading(false)
      }
    }
    void loadCategories()
    return () => {
      ignore = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const load = async (targetPage: number, append: boolean) => {
    if (append) setLoadingMore(true)
    else setLoading(true)
    if (!append) setError(null)
    try {
      const data = await discoverPros({
        q: q.trim() || undefined,
        category: category || undefined,
        subcategory: subcategory || undefined,
        open_now: openNow || undefined,
        bookable_today: bookableToday || undefined,
        has_booking: hasBooking || undefined,
        sort,
        page: targetPage,
        page_size: 12,
      })
      setPage(data.page)
      setHasNext(data.has_next)
      setItems((prev) => (append ? [...prev, ...data.results] : data.results))
    } catch (e: any) {
      if (!append) setItems([])
      setHasNext(false)
      setError(e?.response?.data?.detail || e?.message || 'Failed to load pros')
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }

  useEffect(() => {
    void load(1, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, subcategory, openNow, bookableToday, hasBooking, sort])

  const setBoolParam = (key: string, enabled: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (enabled) next.set(key, '1')
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }

  const setTextParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    const clean = String(value || '').trim()
    if (clean) next.set(key, clean)
    else next.delete(key)
    if (key === 'category') next.delete('subcategory')
    setSearchParams(next, { replace: true })
  }

  const clearAllFilters = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('category')
    next.delete('subcategory')
    next.delete('open_now')
    next.delete('bookable_today')
    next.delete('has_booking')
    next.delete('sort')
    setSearchParams(next, { replace: true })
  }

  const onSortChange = (value: SortKey) => {
    const next = new URLSearchParams(searchParams)
    if (value === 'newest') next.delete('sort')
    else next.set('sort', value)
    setSearchParams(next, { replace: true })
  }

  const sortLabel = useMemo(() => {
    if (sort === 'soonest') return 'Soonest available'
    if (sort === 'distance') return 'Distance'
    if (sort === 'reputation') return 'Rating'
    if (sort === 'alphabetical') return 'Alphabetical'
    return 'Newest'
  }, [sort])

  const activeCategory = useMemo(() => {
    if (!category) return null
    return categories.find((item) => item.slug === category) || null
  }, [categories, category])

  const activeSubcategory = useMemo(() => {
    if (!activeCategory || !subcategory) return null
    return activeCategory.subcategories.find((item) => item.slug === subcategory) || null
  }, [activeCategory, subcategory])

  const openDiscoverBooking = (hostRef: string, displayName: string) => {
    setDiscoverBookingTarget({ hostRef, displayName })
    setDiscoverBookingOpen(true)
  }

  const toggleCategoryExpanded = (slug: string) => {
    setExpandedCategorySlugs((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  const mapDiscoverToCard = (pro: ProDiscoverItem): ProCardViewModel => {
    return {
      handle: pro.handle,
      display_name: pro.display_name,
      title: pro.pro_title || '',
      description: '',
      mode: 'remote',
      available_now: Boolean(pro.open_now),
      skills: Array.isArray(pro.skills) ? pro.skills : [],
      rating_avg: Number(pro.pro_rating || 0),
      rating_count: Number(pro.review_count || 0),
      jobs_count: null,
      earned_pixe: null,
      is_owner: false,
      avatar_url: pro.avatar_url || undefined,
    }
  }

  const canBookFromCard = (pro: ProDiscoverItem): boolean => {
    return Boolean(
      pro.booking?.enabled &&
      pro.booking?.has_services &&
      (pro.booking?.bookable_today || Boolean(pro.booking?.next_available_at)),
    )
  }

  const publicProfilePath = (pro: ProDiscoverItem): string => {
    if (pro.public_id) return `/people/i/${encodeURIComponent(pro.public_id)}`
    return `/p/${encodeURIComponent(pro.handle)}`
  }

  const activeFilterCount = [Boolean(category), Boolean(subcategory), openNow, bookableToday, hasBooking, sort !== 'newest']
    .filter(Boolean)
    .length

  return (
    <div className="min-h-screen">
      <NavBar mobileFixed showMobileModePill={false} />
      <main className="mx-auto max-w-7xl px-4 pt-[calc(var(--nav-h,56px)+12px)] pb-6 md:py-6">
        <section className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setBoolParam('open_now', !openNow)}
              className={`rounded-full border px-3 py-1.5 text-xs ${openNow ? 'border-brand/60 bg-brand/20 text-brand-100' : 'border-white/10 bg-white/5 text-white/80'}`}
            >
              Open now
            </button>
            <button
              type="button"
              onClick={() => setBoolParam('bookable_today', !bookableToday)}
              className={`rounded-full border px-3 py-1.5 text-xs ${bookableToday ? 'border-brand/60 bg-brand/20 text-brand-100' : 'border-white/10 bg-white/5 text-white/80'}`}
            >
              Bookable today
            </button>
            <button
              type="button"
              onClick={() => setBoolParam('has_booking', !hasBooking)}
              className={`rounded-full border px-3 py-1.5 text-xs ${hasBooking ? 'border-brand/60 bg-brand/20 text-brand-100' : 'border-white/10 bg-white/5 text-white/80'}`}
            >
              Has booking
            </button>
            <select
              value={sort}
              onChange={(e) => onSortChange(e.target.value as SortKey)}
              className="rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-xs text-white"
            >
              <option value="newest">Newest</option>
              <option value="soonest">Soonest available</option>
              <option value="reputation">Rating</option>
              <option value="distance">Distance</option>
              <option value="alphabetical">Alphabetical</option>
            </select>
            {activeFilterCount > 0 ? (
              <button
                type="button"
                onClick={clearAllFilters}
                className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10"
              >
                Clear all ({activeFilterCount})
              </button>
            ) : null}
          </div>

          {activeFilterCount > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {category ? (
                <button
                  type="button"
                  onClick={() => setTextParam('category', null)}
                  className="rounded-full border border-brand/50 bg-brand/15 px-3 py-1 text-xs text-brand-100"
                >
                  Category: {activeCategory?.name || category} x
                </button>
              ) : null}
              {subcategory ? (
                <button
                  type="button"
                  onClick={() => setTextParam('subcategory', null)}
                  className="rounded-full border border-brand/50 bg-brand/15 px-3 py-1 text-xs text-brand-100"
                >
                  Subcategory: {activeSubcategory?.name || subcategory} x
                </button>
              ) : null}
              {openNow ? (
                <button type="button" onClick={() => setBoolParam('open_now', false)} className="rounded-full border border-brand/50 bg-brand/15 px-3 py-1 text-xs text-brand-100">
                  Open now x
                </button>
              ) : null}
              {bookableToday ? (
                <button type="button" onClick={() => setBoolParam('bookable_today', false)} className="rounded-full border border-brand/50 bg-brand/15 px-3 py-1 text-xs text-brand-100">
                  Bookable today x
                </button>
              ) : null}
              {hasBooking ? (
                <button type="button" onClick={() => setBoolParam('has_booking', false)} className="rounded-full border border-brand/50 bg-brand/15 px-3 py-1 text-xs text-brand-100">
                  Has booking x
                </button>
              ) : null}
              {sort !== 'newest' ? (
                <button type="button" onClick={() => onSortChange('newest')} className="rounded-full border border-brand/50 bg-brand/15 px-3 py-1 text-xs text-brand-100">
                  Sort: {sortLabel} x
                </button>
              ) : null}
            </div>
          ) : (
            <div className="mt-2 text-xs text-white/60">Browsing all pros. Use filters to narrow results.</div>
          )}
        </section>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="lg:sticky lg:top-24 lg:h-fit">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.14em] text-white/60">
                <span>Categories</span>
                <span>{formatCount(categories.reduce((acc, item) => acc + Number(item.count || 0), 0))}</span>
              </div>
              <div className="flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={() => setTextParam('category', null)}
                  className={`flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-sm ${!category ? 'bg-brand/20 text-brand-100' : 'text-white/80 hover:bg-white/10'}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <CategoryIcon slug="all" className={!category ? 'text-brand-100' : 'text-white/60'} />
                    All categories
                  </span>
                  <span className="text-xs text-white/60">{formatCount(categories.reduce((acc, item) => acc + Number(item.count || 0), 0))}</span>
                </button>
                {categoriesLoading ? (
                  <div className="space-y-1.5 px-1 py-1" role="status" aria-label="Loading categories">
                    {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-7 animate-pulse rounded bg-white/8" />)}
                  </div>
                ) : categories.length === 0 ? (
                  <div className="rounded-lg border border-white/10 bg-black/20 px-2.5 py-2 text-xs text-white/65">
                    No categories available yet.
                  </div>
                ) : (
                  categories.map((cat) => {
                    const isExpanded = expandedCategorySlugs.has(cat.slug) || category === cat.slug
                    return (
                      <div key={cat.slug} className="rounded-lg border border-transparent hover:border-white/10">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleCategoryExpanded(cat.slug)}
                            className="rounded-md px-2 py-2 text-xs text-white/60 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${cat.name}`}
                          >
                            {isExpanded ? <ChevronDown size={14} className="text-white/70" /> : <ChevronRight size={14} className="text-white/70" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => setTextParam('category', cat.slug)}
                            className={`flex min-h-10 min-w-0 flex-1 items-center justify-between rounded-lg border-l-2 px-2.5 py-2 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 ${category === cat.slug ? 'border-l-brand bg-brand/20 text-brand-100' : 'border-l-transparent text-white/80 hover:bg-white/10'}`}
                          >
                            <span className="inline-flex min-w-0 items-center gap-2">
                              <CategoryIcon slug={cat.slug} className={category === cat.slug ? 'text-brand-100' : 'text-white/60'} />
                              <span className="truncate">{cat.name}</span>
                            </span>
                            <span className="text-xs text-white/60">{formatCount(Number(cat.count || 0))}</span>
                          </button>
                        </div>
                        {isExpanded && cat.subcategories?.length ? (
                          <div className="mt-1 space-y-1 pl-8">
                            {cat.subcategories
                              .slice()
                              .sort((a, b) => Number(a.order || 0) - Number(b.order || 0) || a.name.localeCompare(b.name))
                              .map((sub) => (
                                <button
                                  key={sub.slug}
                                  data-testid={`subcategory-row-${cat.slug}-${sub.slug}`}
                                  type="button"
                                  onClick={() => {
                                    setTextParam('category', cat.slug)
                                    setTextParam('subcategory', sub.slug)
                                  }}
                                  className={`flex min-h-9 w-full items-center justify-between rounded-md border-l-2 pl-4 pr-2 py-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 ${subcategory === sub.slug ? 'border-l-brand bg-brand/15 text-brand-100' : 'border-l-transparent text-white/75 hover:bg-white/10'}`}
                                >
                                  <span className="inline-flex items-center gap-2">
                                    <span
                                      aria-hidden="true"
                                      className={`h-1 w-1 rounded-full ${subcategory === sub.slug ? 'bg-brand-200' : 'bg-white/35'}`}
                                    />
                                    {sub.name}
                                  </span>
                                  <span className="text-[11px] text-white/55">{formatCount(Number(sub.count || 0))}</span>
                                </button>
                              ))}
                          </div>
                        ) : null}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </aside>

          <section>
            {loading ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-64 animate-pulse rounded-xl bg-white/5" />
                ))}
              </div>
            ) : null}

            {!loading && error ? (
              <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-200">
                Could not load pros right now.
                <button type="button" onClick={() => void load(1, false)} className="ml-3 rounded-full border border-red-300/30 px-3 py-1 text-xs">
                  Retry
                </button>
              </div>
            ) : null}

            {!loading && !error && items.length === 0 ? (
              <div className="rounded-xl border border-white/10 bg-white/5 p-5 text-sm text-white/70">
                <div className="mb-1 text-white">No pros match these filters yet.</div>
                <div>Try clearing one or two filters to see more results.</div>
                {activeFilterCount > 0 ? (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="mt-3 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10"
                  >
                    Clear filters
                  </button>
                ) : null}
              </div>
            ) : null}

            {!loading && !error && items.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {items.map((pro) => (
                  <div key={`${pro.handle}-${pro.public_id || ''}`} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <ProPublicCard
                      pro={mapDiscoverToCard(pro)}
                      variant="browse"
                      show_book_cta={false}
                      right_actions={(
                        <>
                          <Link
                            to={publicProfilePath(pro)}
                            className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/85 hover:bg-white/10"
                          >
                            Open profile
                          </Link>
                          {canBookFromCard(pro) ? (
                            <button
                              type="button"
                              onClick={() => openDiscoverBooking(pro.handle, pro.display_name)}
                              className="rounded-full border border-brand/40 bg-brand/20 px-3 py-1.5 text-xs font-semibold text-brand-100 hover:bg-brand/30"
                            >
                              Book now
                            </button>
                          ) : null}
                        </>
                      )}
                    />
                    {pro.booking?.next_available_at ? (
                      <div className="mt-2 text-xs text-brand-100">
                        Next available: {new Date(pro.booking.next_available_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {hasNext && !loading ? (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => void load(page + 1, true)}
                  disabled={loadingMore}
                  className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 disabled:opacity-60"
                >
                  {loadingMore ? 'Loading more pros...' : 'Load more pros'}
                </button>
              </div>
            ) : null}
          </section>
        </div>
      </main>
      <DiscoverBookingModal
        open={discoverBookingOpen}
        hostType="pro"
        hostRef={discoverBookingTarget?.hostRef || ''}
        displayName={discoverBookingTarget?.displayName || 'Pro'}
        onClose={() => setDiscoverBookingOpen(false)}
        onBooked={() => {
          setDiscoverBookingOpen(false)
          void load(1, false)
        }}
      />
    </div>
  )
}
